import { composeProgress } from "apollo/utils";
import { GET_AUCTION_FORMS } from "core/apollo/graphql";
import {
  DOCS_FORM_NAMES,
  SEARCH_ACTION_DELETE_FORM,
  SEARCH_ACTION_DELETE_REHAB_FORMS,
  SEARCH_ACTION_DELETE_TRANSITIONAL_CARE_FORM,
} from "core/consts";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import { Auction, DocsFormName, RehabFormName } from "core/types";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { getRehabFormConfig } from "dsl/ecosystems/PatientForms/RehabForms/configs";
import useSearchAction from "dsl/hooks/useSearchAction";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useDeleteConfirmation({
  auction,
  formName,
}: {
  auction: Auction;
  formName: DocsFormName;
}) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [deleteRehabForm, deleteRehabFormProgress, resetProgress] =
    useSearchAction({
      actionType: SEARCH_ACTION_DELETE_REHAB_FORMS,
      refetchQueries: [
        {
          context: { encryptionContext },
          query: GET_AUCTION_FORMS,
          variables: { auctionId: auction.id },
        },
      ],
    });
  const [deleteTransitionalCareForm, deleteTransitionalCareFormProgress] =
    useSearchAction({
      actionType: SEARCH_ACTION_DELETE_TRANSITIONAL_CARE_FORM,
      refetchQueries: [
        {
          context: { encryptionContext },
          query: GET_AUCTION_FORMS,
          variables: { auctionId: auction.id },
        },
      ],
    });

  const [deleteForm, deleteFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_AUCTION_FORMS,
        variables: {
          auctionId: auction.id,
        },
      },
    ],
  });

  const { formSubType, formType } =
    formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE
      ? { formSubType: undefined, formType: undefined }
      : getRehabFormConfig(formName as RehabFormName) ?? {};

  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction.id,
    formSubType,
    formType,
  });

  const onDelete = async () => {
    try {
      switch (formName) {
        case DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT:
        case DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY:
          await deleteForm({
            auction,
            context: {
              form_type: FORM_NAME_TO_FILE_TYPE[formName],
            },
          });
          break;
        case DOCS_FORM_NAMES.TRANSITIONAL_CARE:
          await deleteTransitionalCareForm({
            auction,
          });
          break;
        default:
          await deleteRehabForm({
            auction,
            context: {
              rehab_form_type: formType,
            },
          });
          break;
      }

      await deleteUnsharedFile();
      resetProgress();
    } catch (err) {
      console.error(`error deleting ${formName} form: ${getErrorMessage(err)}`);
    }
  };

  return {
    deleteFormProgress: composeProgress([
      deleteRehabFormProgress,
      deleteTransitionalCareFormProgress,
      deleteFormProgress,
    ]),
    onDelete,
  };
}
